// DetailPanel — 우측 슬라이드 상세 패널 컴포넌트 (U42 + U43 인라인 편집).
//
// 동작 (2026-10-02 개편):
//   - 데스크톱(≥ 1024px): Layout 우측 고정 컬럼 (오버레이 아님). 외부 클릭으로 닫히지 않음
//     → 비밀번호 보기·복사 등 패널 밖 포커스 이동 시 패널이 꺼지던 문제 해소
//   - showEmpty=true면 항목 미선택 시 빈 상태 안내를 표시
//   - 768~1023px: 우측 슬라이드 오버레이 / 모바일(< 768px): 풀스크린 시트 (backdrop 클릭으로 닫기)
//   - 폭: 320~600px, 좌측 resize handle로 조절. localStorage 저장.
//   - 닫기: Esc·X 버튼·locker:close-overlay 이벤트
//   - 헤더 수정(연필) → ItemEditForm / 삭제(휴지통) → 휴지통 이동 + Undo
//
// U43 인라인 편집:
//   - 이름: DetailHeader에서 InlineEdit 적용
//   - 메모: multiline InlineEdit
//   - 그룹: 텍스트 표시 (향후 select 확장)
//   - 태그: TagChipInput
//   - payload 필드: PayloadFields (타입별 InlineEdit)
//   - Optimistic save: useItemMutations.updateItem onMutate 낙관적 갱신
//
// U57 모바일 swipe-down:
//   - 모바일 풀스크린(< 768px)에서 위→아래 swipe로 패널 닫기
//   - 드래그 핸들 영역(상단 40px) 또는 헤더에서 touchstart 감지
//   - 조건: ΔY ≥ 50px && 속도 ≥ 0.3px/ms → 닫기
//   - 진행 중 translateY 시각 피드백 / 미달 시 spring back (240ms ease-out)

import { useCallback, useEffect, useRef, useState } from 'react';
import { MousePointerClick } from 'lucide-react';
import type { ReactNode } from 'react';
import { DetailHeader } from './DetailHeader';
import { PayloadFields } from './PayloadFields';
import { ItemEditForm } from './ItemEditForm';
import { Kbd } from '../../ui/Kbd';
import { Spinner } from '../Spinner';
import { TagChipInput } from '../TagChipInput';
import { InlineEdit } from '../../ui/InlineEdit';
import { useDetailPanelItem } from '../../hooks/useDetailPanel';
import { useItemMutations } from '../../hooks/useItems';
import { useToast } from '../Toast';
import { formatApiError } from '../../api/client';
import { SHORTCUT_EVENT_CLOSE_OVERLAY } from '../../hooks/useGlobalShortcuts';
import type { ItemType } from '../../api/items';

interface DetailPanelProps {
  /** 현재 열린 항목 ID. null이면 패널 닫힘. */
  itemId: number | null;
  /** 패널 닫기 콜백 */
  onClose: () => void;
  /** 패널 폭(px) */
  panelWidth: number;
  /** resize 콜백 (새 폭 전달) */
  onWidthChange: (w: number) => void;
  /** 이전 항목으로 이동 (없으면 버튼 숨김) */
  onPrev?: () => void;
  /** 다음 항목으로 이동 (없으면 버튼 숨김) */
  onNext?: () => void;
  /** 추가 body 슬롯 컨텐츠 */
  extraContent?: ReactNode;
  /** 항목 미선택 시 빈 상태 안내 표시 (데스크톱 고정 컬럼용) */
  showEmpty?: boolean;
}

// ─── 내부 컴포넌트: 패널 컨텐츠 (itemId 보장) ─────────────────

interface PanelContentProps {
  itemId: number;
  onClose: () => void;
  extraContent?: ReactNode;
}

/**
 * 패널이 열린 상태에서 데이터를 로드·렌더하는 내부 컴포넌트.
 * itemId가 보장되어 있어 null 체크를 최소화한다.
 */
function DetailPanelContent({ itemId, onClose, extraContent }: PanelContentProps) {
  const { data, isLoading } = useDetailPanelItem(itemId);
  const { pinItem, unpinItem, touchItem, updateItem, deleteItem } = useItemMutations();
  const toast = useToast();
  // 수정 모드 (전체 필드 폼)
  const [editing, setEditing] = useState(false);

  // 다른 항목으로 바뀌면 수정 모드 해제
  useEffect(() => {
    setEditing(false);
  }, [itemId]);

  // 패널이 열릴 때 touch (last_used_at 갱신)
  useEffect(() => {
    touchItem.mutate(itemId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId]);

  const handlePinToggle = useCallback(() => {
    if (!data) return;
    const isPinned = data.item.pinned === 1;
    if (isPinned) {
      unpinItem.mutate(itemId);
    } else {
      pinItem.mutate(itemId);
    }
  }, [data, itemId, pinItem, unpinItem]);

  // ─── 필드 저장 헬퍼 ──────────────────────────────────────

  /** 이름 저장 */
  const handleSaveName = useCallback(
    async (newName: string) => {
      try {
        await updateItem.mutateAsync({ id: itemId, patch: { name: newName } });
      } catch (err) {
        toast.error(formatApiError(err, '이름 저장 실패'));
        throw err; // InlineEdit에 에러 전파
      }
    },
    [itemId, updateItem, toast],
  );

  /** payload 단일 필드 저장 */
  const handleSavePayloadField = useCallback(
    async (key: string, value: unknown) => {
      if (!data) return;
      // value는 PayloadFields가 서버 형식으로 변환한 값. undefined = 선택 필드 비우기(키 제거)
      const newPayload: Record<string, unknown> = { ...data.payload, [key]: value };
      if (value === undefined) delete newPayload[key];
      try {
        await updateItem.mutateAsync({ id: itemId, patch: { payload: newPayload } });
      } catch (err) {
        toast.error(formatApiError(err, `${key} 저장 실패`));
        throw err;
      }
    },
    [data, itemId, updateItem, toast],
  );

  /** 태그 저장 — 이름 배열로 전체 교체 (PATCH tags). 실패 시 토스트 후 재throw */
  const handleSaveTags = useCallback(
    async (names: string[]) => {
      try {
        await updateItem.mutateAsync({ id: itemId, patch: { tags: names } });
      } catch (err) {
        toast.error(formatApiError(err, '태그 저장 실패'));
        throw err;
      }
    },
    [itemId, updateItem, toast],
  );

  /** 삭제 — 확인 후 휴지통 이동 (Undo 토스트는 deleteItem이 등록) */
  const handleDelete = useCallback(() => {
    if (!data) return;
    const name = data.item.name;
    if (!window.confirm(`"${name}" 항목을 삭제할까요?
휴지통으로 이동하며 30일 안에 복원할 수 있습니다.`)) return;
    deleteItem.mutate(
      { id: itemId, name },
      {
        onSuccess: () => onClose(),
        onError: (err) => toast.error(formatApiError(err, '삭제 실패')),
      },
    );
  }, [data, itemId, deleteItem, onClose, toast]);

  if (isLoading || !data) {
    return (
      <div className="detail-panel__loading">
        <Spinner />
      </div>
    );
  }

  const { item, payload, tags, events } = data;

  return (
    <>
      {/* 헤더 — 이름 인라인 편집 포함 */}
      <DetailHeader
        name={item.name}
        type={item.type as ItemType}
        pinned={item.pinned}
        onPinToggle={handlePinToggle}
        onClose={onClose}
        onSaveName={handleSaveName}
        onEdit={() => setEditing((v) => !v)}
        editing={editing}
        onDelete={handleDelete}
      />

      {/* 본문 스크롤 영역 */}
      <div className="detail-panel__body">
        {editing ? (
          <ItemEditForm item={item} payload={payload} tags={tags} onDone={() => setEditing(false)} />
        ) : (
        <>
        {/* 타입 배지 */}
        <div className="detail-panel__section">
          <span className="badge badge--type">{item.type}</span>
        </div>

        {/* Payload 필드 (인라인 편집) */}
        {Object.keys(payload).length > 0 && (
          <div className="detail-panel__section">
            <h3 className="detail-panel__section-title">페이로드</h3>
            <PayloadFields
              itemType={item.type}
              payload={payload}
              onSave={handleSavePayloadField}
            />
          </div>
        )}

        {/* 태그 — TagChipInput */}
        <div className="detail-panel__section">
          <h3 className="detail-panel__section-title">태그</h3>
          <TagChipInput
            value={tags}
            onChange={handleSaveTags}
            placeholder="태그 추가 (Enter)"
          />
        </div>

        {/* 이벤트 */}
        {events.length > 0 && (
          <div className="detail-panel__section">
            <h3 className="detail-panel__section-title">이벤트</h3>
            <ul className="detail-panel__events">
              {events.map((ev) => (
                <li key={ev.id} className="detail-panel__event-item">
                  <span className="badge">{ev.kind}</span>
                  {ev.due_at !== null && (
                    <span className="muted">
                      {' '}마감: {new Date(ev.due_at).toLocaleDateString('ko-KR')}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* 메타 */}
        <div className="detail-panel__section detail-panel__meta muted">
          <span>사용 {item.use_count}회</span>
          {item.last_used_at !== null && (
            <span> · 최근 {new Date(item.last_used_at).toLocaleDateString('ko-KR')}</span>
          )}
          <span> · 생성 {new Date(item.created_at).toLocaleDateString('ko-KR')}</span>
        </div>

        {/* 추가 슬롯 */}
        {extraContent}
        </>
        )}
      </div>
    </>
  );
}

// ─── 메인 컴포넌트 ─────────────────────────────────────────────

/**
 * Detail Panel — 우측 고정 슬라이드 패널.
 * itemId가 null이면 패널이 닫힌 상태(translateX(100%))로 렌더된다.
 */
export function DetailPanel({
  itemId,
  onClose,
  panelWidth,
  onWidthChange,
  onPrev,
  onNext,
  extraContent,
  showEmpty = false,
}: DetailPanelProps) {
  const isOpen = itemId !== null;
  const isEmptyVisible = !isOpen && showEmpty;
  const panelRef = useRef<HTMLDivElement>(null);

  // resize 드래그 상태 (ref — 렌더 트리거 불필요)
  const resizingRef = useRef(false);
  const resizeStartXRef = useRef(0);
  const resizeStartWidthRef = useRef(panelWidth);

  // ─── 모바일 swipe-down 상태 ────────────────────────────────
  // translateY 값 (px) — 드래그 중 시각 피드백용
  const [swipeTranslateY, setSwipeTranslateY] = useState(0);
  // 스프링 백 전환 활성 여부
  const [swipeTransition, setSwipeTransition] = useState(false);
  // touch 시작 좌표·시간
  const swipeTouchStartY = useRef(0);
  const swipeTouchStartTime = useRef(0);

  // Esc·locker:close-overlay 이벤트 처리
  useEffect(() => {
    if (!isOpen) return;

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    const handleCloseEvent = () => {
      onClose();
    };

    document.addEventListener('keydown', handleKey);
    window.addEventListener(SHORTCUT_EVENT_CLOSE_OVERLAY, handleCloseEvent);

    return () => {
      document.removeEventListener('keydown', handleKey);
      window.removeEventListener(SHORTCUT_EVENT_CLOSE_OVERLAY, handleCloseEvent);
    };
  }, [isOpen, onClose]);

  // 외부 클릭 닫기는 제거 (2026-10-02) — 고정 컬럼에서 패널 밖 클릭·포커스 이동으로 꺼지던 문제.
  // 모바일 시트는 backdrop 클릭으로만 닫는다.

  // 패널이 열릴 때 swipe 상태 초기화
  useEffect(() => {
    if (isOpen) {
      setSwipeTranslateY(0);
      setSwipeTransition(false);
    }
  }, [isOpen, itemId]);

  // ─── 모바일 swipe-down 핸들러 ──────────────────────────────

  /**
   * touchstart — 시작 좌표·시간 기록, transition 해제.
   * 드래그 핸들(상단 40px)·헤더 영역 터치 시 swipe 감지 활성화.
   */
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    swipeTouchStartY.current = touch.clientY;
    swipeTouchStartTime.current = Date.now();
    setSwipeTransition(false);
  }, []);

  /**
   * touchmove — 아래 방향(deltaY > 0)일 때만 translateY 시각 피드백.
   * 위 방향은 무시 (스크롤 허용).
   */
  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    const deltaY = touch.clientY - swipeTouchStartY.current;
    if (deltaY > 0) {
      setSwipeTranslateY(deltaY);
    }
  }, []);

  /**
   * touchend — swipe 기준 판정.
   * ΔY ≥ 50px && 속도 ≥ 0.3px/ms → 닫기
   * 미달 → spring back (transition 240ms ease-out)
   */
  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      const touch = e.changedTouches[0];
      const deltaY = touch.clientY - swipeTouchStartY.current;
      const elapsed = Date.now() - swipeTouchStartTime.current;
      // elapsed가 0이면 사실상 즉각적인 플릭 — 충분히 빠른 것으로 간주 (Infinity)
      const velocity = elapsed > 0 ? deltaY / elapsed : Infinity;

      const shouldClose = deltaY >= 50 && velocity >= 0.3;

      if (shouldClose) {
        // 닫기 — 상태 초기화 후 콜백 호출
        setSwipeTranslateY(0);
        onClose();
      } else {
        // spring back
        setSwipeTransition(true);
        setSwipeTranslateY(0);
      }
    },
    [onClose],
  );

  // resize handle 마우스 다운
  const handleResizeMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      resizingRef.current = true;
      resizeStartXRef.current = e.clientX;
      resizeStartWidthRef.current = panelWidth;

      const handleMouseMove = (ev: MouseEvent) => {
        if (!resizingRef.current) return;
        // 패널은 우측에 고정이므로 마우스 좌측 이동 = 패널 폭 증가
        const delta = resizeStartXRef.current - ev.clientX;
        const newWidth = resizeStartWidthRef.current + delta;
        onWidthChange(newWidth);
      };

      const handleMouseUp = () => {
        resizingRef.current = false;
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [panelWidth, onWidthChange],
  );

  return (
    <>
      {/* 배경 오버레이 — 모바일 풀스크린 시 외부 딤처리 */}
      {isOpen && <div className="detail-panel__backdrop" aria-hidden="true" onClick={onClose} />}

      {/* 패널 본체 */}
      <div
        ref={panelRef}
        className={`detail-panel${isOpen ? ' detail-panel--open' : ''}${isEmptyVisible ? ' detail-panel--empty' : ''}`}
        style={
          {
            '--panel-width': `${panelWidth}px`,
            // 모바일 swipe-down 시각 피드백: translateY 적용
            // CSS transform은 detail-panel의 기존 translateX와 함께 적용됨
            ...(swipeTranslateY > 0 || swipeTransition
              ? {
                  transform: `translateY(${swipeTranslateY}px)`,
                  transition: swipeTransition ? 'transform 240ms ease-out' : 'none',
                }
              : {}),
          } as React.CSSProperties
        }
        role="complementary"
        aria-label="항목 상세"
        aria-hidden={!isOpen && !isEmptyVisible}
        data-testid="detail-panel"
        // 모바일 swipe-down touch 핸들러
        onTouchStart={isOpen ? handleTouchStart : undefined}
        onTouchMove={isOpen ? handleTouchMove : undefined}
        onTouchEnd={isOpen ? handleTouchEnd : undefined}
      >
        {/* 모바일 드래그 핸들 — 시각적 swipe 힌트 바 */}
        {isOpen && (
          <div
            className="detail-panel__drag-handle"
            aria-hidden="true"
            data-testid="detail-panel-drag-handle"
          />
        )}

        {/* Resize handle — 패널 좌측 가장자리 (데스크톱에서만 표시) */}
        <div
          className="detail-panel__resize-handle"
          onMouseDown={handleResizeMouseDown}
          aria-hidden="true"
          data-testid="detail-panel-resize-handle"
          title="드래그해서 크기 조절"
        />

        {/* 이전/다음 이동 버튼 */}
        {isOpen && (onPrev !== undefined || onNext !== undefined) && (
          <div className="detail-panel__nav-btns">
            {onPrev !== undefined && (
              <button
                type="button"
                className="detail-panel__nav-btn"
                onClick={onPrev}
                aria-label="이전 항목"
                title="이전 항목"
              >
                ‹
              </button>
            )}
            {onNext !== undefined && (
              <button
                type="button"
                className="detail-panel__nav-btn"
                onClick={onNext}
                aria-label="다음 항목"
                title="다음 항목"
              >
                ›
              </button>
            )}
          </div>
        )}

        {/* 빈 상태 — 항목 미선택 (데스크톱 고정 컬럼) */}
        {isEmptyVisible && (
          <div className="detail-panel__empty" data-testid="detail-panel-empty">
            <div className="detail-panel__empty-icon" aria-hidden="true">
              <MousePointerClick size={28} />
            </div>
            <p className="detail-panel__empty-title">항목을 선택하세요</p>
            <p className="detail-panel__empty-desc">
              목록에서 항목을 클릭하면 상세 정보가 여기에 표시됩니다.
            </p>
            <ul className="detail-panel__empty-tips">
              <li><Kbd>Ctrl</Kbd> <Kbd>K</Kbd> <span>빠른 검색·명령</span></li>
              <li><Kbd>/</Kbd> <span>검색창으로 이동</span></li>
              <li><Kbd>?</Kbd> <span>단축키 전체 보기</span></li>
            </ul>
          </div>
        )}

        {/* 컨텐츠 — itemId가 있을 때만 마운트 */}
        {isOpen && itemId !== null && (
          <DetailPanelContent
            itemId={itemId}
            onClose={onClose}
            extraContent={extraContent}
          />
        )}
      </div>
    </>
  );
}
